import Image from "next/image";
import sharedService from "@/services/shared-service";
import {useEffect, useRef, useState} from "react";
import {getGospel} from "@/services/home-service";
import {
    Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure,
    ScrollShadow, Button, Link
} from "@nextui-org/react";
import {isEmpty} from "lodash";
import NotFound from "./not-found";

export default function ClergiesBlock({clergies, actions, hasMore}) {
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [quotes, setQuotes] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const sentinelRef = useRef(null);

    // Thiết lập Intersection Observer hỗ trợ infinite scroll
    useEffect(() => {
        if (!sentinelRef.current || !actions?.onLoadMore) return;

        if (!hasMore) return;

        let scrollableContainer = null;
        let element = sentinelRef.current.parentElement;
        while (element) {
            const overflow = window.getComputedStyle(element).overflowY;
            if (overflow === 'auto' || overflow === 'scroll') {
                scrollableContainer = element;
                break;
            }
            element = element.parentElement;
        }

        const obs = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    // Nếu đang load thêm thì không gọi onLoadMore nữa
                    if (isLoadingMore) return;
                    setIsLoadingMore(true);
                    //Gọi onLoadMore  và đặt lại trạng thái loading sau khi hoàn tất
                    Promise.resolve(actions.onLoadMore()).finally(() => setIsLoadingMore(false));
                }
            });
        }, {root: scrollableContainer, rootMargin: '100px', threshold: 0.01});

        obs.observe(sentinelRef.current);

        return () => obs.disconnect();

    }, [actions?.onLoadMore, hasMore]);

    if (!clergies.value?.length) {
        return (
            <div className="h-[200px] md:h-[300px] mt-2">
                <NotFound query={"trong danh sách Linh mục"}/>
            </div>
        );
    }

    async function openCode(code) {
        let quotes = await getGospel(code);
        setQuotes(quotes);
        onOpen();
    }


    return <>
        {clergies.value?.map((clergyData) => {
            const isRIP = clergyData.priestStatus === "rip";
            const name = (clergyData.lastName ? clergyData.lastName + ' ' : '') + (clergyData.middleName ? clergyData.middleName : '') + (clergyData.firstName ? ' ' + clergyData.firstName : '');
            const clergyName = (clergyData?.saintName) ? clergyData?.saintName + ' ' + name : '';
            const anniversaryMap = (clergyData?.anniversaries || []).reduce((acc, item) => {
                if (!item?.name) return acc;

                acc[item.name] = item;

                return acc;
            }, {});
            return (
                <>
                    <div className={`sm:flex border-b-2 border-primary p-3 bg-white rounded-md shadow-md mb-2`}>
                        <div className="contain-img my-auto">
                            <Image
                                className="w-[50%] lg:!w-full mx-auto !relative object-contain !h-[unset] rounded-[10px]"
                                alt={clergyName}
                                width={120}
                                height={120}
                                src={
                                    sharedService.isValidUrl(clergyData?.photo)
                                        ? `${clergyData?.photo?.replace(
                                            "public/",
                                            ""
                                        )}`
                                        : clergyData?.photo?.replace(
                                            "public/",
                                            ""
                                        )
                                            ? `${
                                                GlobalSettings.Settings.Server
                                            }/${clergyData?.photo?.replace(
                                                "public/",
                                                ""
                                            )}`
                                            : "/assets/images/logo-for-web.png"
                                }
                            />
                        </div>
                        <div className="contain-infos ml-3 w-full pr-5">
                            <div className="info--title mt-3">
                                {clergyData?.saintName && (
                                    <>
                                        <h4 className="font-bold text-blue-700 mb-3 text-xl">
                                            Linh Mục {clergyName}
                                        </h4>
                                    </>
                                )}

                                {clergyData?.parable && (
                                    <p className="mb-3">
                                        Châm ngôn:{" "}
                                        <span
                                            className="text-violet-500 cursor-pointer"
                                            onClick={() =>
                                                openCode(
                                                    clergyData?.parable
                                                        ?.split("(")?.[1]
                                                        ?.split(")")?.[0]
                                                        ?.trim()
                                                )
                                            }
                                        >
                                            {clergyData?.parable}
                                        </span>
                                    </p>
                                )}

                                <hr/>
                            </div>
                            <div className="info-further grid grid-cols-2 mt-3">
                                <div className="col-span-2 lg:col-span-1">
                                    {anniversaryMap["Sinh nhật"]?.date && (
                                        <p className="mb-3">
                                            Sinh nhật:{" "}
                                            {sharedService.formatISODate(
                                                anniversaryMap["Sinh nhật"].date,
                                                "DD/MM/YYYY"
                                            )}
                                        </p>
                                    )}

                                    {anniversaryMap["Truyền Chức Phó Tế"]?.date && (
                                        <p className="mb-3">
                                            Truyền Chức Phó Tế:{" "}
                                            {sharedService.formatISODate(
                                                anniversaryMap["Truyền Chức Phó Tế"].date,
                                                "DD/MM/YYYY"
                                            )}
                                        </p>
                                    )}

                                    {anniversaryMap["Truyền Chức Linh Mục"]?.date && (
                                        <p className="mb-3">
                                            Truyền Chức Linh Mục:{" "}
                                            {sharedService.formatISODate(
                                                anniversaryMap["Truyền Chức Linh Mục"].date,
                                                "DD/MM/YYYY"
                                            )}
                                        </p>
                                    )}
                                    {/* Mobile layout: start */}
                                    <p className="mb-3 lg:hidden">
                                        {isRIP ? (
                                            <>
                                                <span>Tình trạng: Đã Mất</span>
                                            </>
                                        ) : (
                                            <>
                                                Chức vụ:{" "}
                                                <span
                                                    className={
                                                        clergyData?.position
                                                            ? ""
                                                            : "italic"
                                                    }
                                                >
                                                    {clergyData?.position ||
                                                        "Đang cập nhật"}
                                                </span>
                                            </>
                                        )}
                                    </p>
                                    {!isRIP && (
                                        <>
                                            <p className="lg:hidden">
                                                <Link
                                                    href={`/nha-tho/${clergyData?.workplaceId}`}
                                                >
                                                    Nơi phục vụ:{" "}
                                                    {clergyData?.workplaceName}
                                                </Link>
                                            </p>
                                        </>
                                    )}
                                    {/* Mobile layout: end */}
                                </div>

                                <div className="hidden lg:block">
                                    <p className="mb-3">
                                        {clergyData.priestStatus === "rip" ? (
                                            <span>Tình trạng: Đã Mất</span>
                                        ) : (
                                            <>
                                                Chức vụ:{" "}
                                                <span
                                                    className={
                                                        clergyData?.position
                                                            ? ""
                                                            : "italic"
                                                    }
                                                >
                                                    {clergyData?.position ||
                                                        "Đang cập nhật"}
                                                </span>
                                            </>
                                        )}
                                    </p>

                                    {!isRIP && <>
                                        <p>
                                            {/* TODO: Active redirect to Church Detail page */}
                                            <Link
                                                href={`/nha-tho/${clergyData?.workplaceId}`}
                                                className=""
                                            >
                                                Nơi phục vụ: {clergyData?.workplaceName}
                                            </Link>
                                        </p>
                                    </>}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            );
        })}

        {/* đánh dấu cho infinite scroll */}
        <div ref={sentinelRef}/>

        {isLoadingMore && (
            <div className="flex justify-center p-4">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-gray-900"></div>
            </div>
        )}

        {!hasMore && (clergies.value?.length ?? 0) > 0 && !isLoadingMore && (
            <div className="flex justify-center p-4 text-sm text-gray-500 italic">
                Đã hiển thị tất cả kết quả phù hợp
            </div>
        )}

        <Modal className="p-10" size="xl" isOpen={isOpen} onClose={onClose}>
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">Tin Mừng</ModalHeader>
                        <ScrollShadow hideScrollBar={true} className="w-full h-[32rem]">
                            <ModalBody className="inline">
                                <>
                                    {
                                        !isEmpty(quotes) ? quotes?.map(q => {
                                            return (
                                                <span key={q.sentenceNo} className="text-medium inline"><p
                                                    className="align-text-top italic text-[10px] inline">[{q.sentenceNo}]</p>{q.content}</span>
                                            );
                                        }) : "Không tìm thấy dữ liệu"
                                    }
                                </>
                            </ModalBody>
                        </ScrollShadow>
                        <ModalFooter>
                            <Button variant="light" onPress={onClose}>
                                Đóng
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>

        {/*{*/}
        {/*    isPagination && clergies.pageData?.pageCount > 1 && <>*/}
        {/*        <div className="flex justify-center p-5">*/}
        {/*            <PaginationCustomAjax asPath={router.asPath} pageData={clergies.pageData}*/}
        {/*                                  actions={{setCurrentPage: actions.setCurrentPage}}/>*/}
        {/*        </div>*/}
        {/*    </>*/}
        {/*}*/}
    </>
}
